import { useRef, useState } from "react";
import { Paperclip, Smile, Send, X, FileText, Image as ImageIcon } from "lucide-react";
import { api, API_BASE, formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/lib/toast";

const EMOJIS = [
  "👍","🙏","🎉","🔥","✅","❌","⚠️","📦","🚚","🧱",
  "💪","👀","😀","😂","🙌","🤝","💡","📝","📞","✉️",
  "⏰","📅","💰","🛠️","🏗️","⭐","❤️","👋","🤔","🚀",
];

/**
 * Reusable message composer with attachment + emoji support.
 *
 * Props:
 *   placeholder, onSend(body, attachments[]), sending
 *   attachments is an array of { url, filename, content_type, size }
 */
export default function MessageComposer({ placeholder = "Type a message…", onSend, sending = false }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);

  const insertEmoji = (e) => {
    setBody((b) => b + e);
    setShowEmoji(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setAttachments((prev) => [...prev, data]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (!body.trim() && attachments.length === 0) return;
    await onSend(body.trim(), attachments);
    setBody("");
    setAttachments([]);
  };

  return (
    <form onSubmit={submit} className="relative space-y-2">
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="composer-attachments">
          {attachments.map((a, i) => (
            <AttachmentChip key={i} att={a} onRemove={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} />
          ))}
        </div>
      )}
      <div className="flex items-end gap-2">
        <button
          type="button"
          data-testid="composer-attach"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="h-10 w-10 flex items-center justify-center rounded-lg border border-zinc-300 bg-white text-zinc-600 hover:text-brand-700 hover:border-brand-500 transition-colors flex-shrink-0"
          title="Attach file"
        >
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" hidden onChange={onPickFile} data-testid="composer-file-input" />

        <div className="relative flex-shrink-0">
          <button
            type="button"
            data-testid="composer-emoji"
            onClick={() => setShowEmoji((s) => !s)}
            className="h-10 w-10 flex items-center justify-center rounded-lg border border-zinc-300 bg-white text-zinc-600 hover:text-brand-700 hover:border-brand-500 transition-colors"
            title="Insert emoji"
          >
            <Smile className="w-4 h-4" />
          </button>
          {showEmoji && (
            <div data-testid="composer-emoji-picker" className="absolute bottom-12 left-0 z-30 bg-white border border-zinc-200 rounded-xl shadow-lg p-2 grid grid-cols-10 gap-1 w-80">
              {EMOJIS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => insertEmoji(e)}
                  className="w-7 h-7 flex items-center justify-center hover:bg-zinc-100 rounded text-lg"
                >{e}</button>
              ))}
            </div>
          )}
        </div>

        <input
          ref={inputRef}
          data-testid="message-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder}
          className="es-input flex-1"
        />
        <button
          type="submit"
          data-testid="message-send"
          disabled={sending || uploading || (!body.trim() && attachments.length === 0)}
          className="es-btn-primary"
        >
          <Send className="w-4 h-4" /> Send
        </button>
      </div>
      {uploading && <div className="text-xs text-zinc-500">Uploading…</div>}
    </form>
  );
}

function AttachmentChip({ att, onRemove }) {
  const isImage = (att.content_type || "").startsWith("image/");
  return (
    <div className="flex items-center gap-2 px-2.5 py-1.5 bg-zinc-100 border border-zinc-200 rounded-lg">
      {isImage ? <ImageIcon className="w-3.5 h-3.5 text-zinc-500" /> : <FileText className="w-3.5 h-3.5 text-zinc-500" />}
      <span className="text-xs font-medium text-zinc-700 max-w-[180px] truncate">{att.filename}</span>
      <button type="button" onClick={onRemove} className="text-zinc-400 hover:text-red-600"><X className="w-3 h-3" /></button>
    </div>
  );
}

/** Renders attachments inline inside a message bubble. */
export function MessageAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-testid="message-attachments">
      {attachments.map((a, i) => {
        // Attachments are stored as urls (strings) by current backend; if so, render as link
        if (typeof a === "string") return <FileLink key={i} url={a} filename={a.split("/").pop()} />;
        const url = a.url?.startsWith("http") ? a.url : `${API_BASE.replace("/api", "")}${a.url}`;
        const isImage = (a.content_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(a.filename || "");
        if (isImage) {
          return (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
              <img src={url} alt={a.filename} className="max-h-40 rounded-lg border border-zinc-200" />
            </a>
          );
        }
        return <FileLink key={i} url={url} filename={a.filename} size={a.size} />;
      })}
    </div>
  );
}

function FileLink({ url, filename, size }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg text-xs hover:border-brand-500 hover:text-brand-700 transition-colors"
    >
      <FileText className="w-3.5 h-3.5 text-zinc-500" />
      <span className="font-medium truncate max-w-[200px]">{filename}</span>
      {size && <span className="text-zinc-400">· {(size / 1024).toFixed(0)} KB</span>}
    </a>
  );
}
